import { Box, Button, Modal, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow, Typography, Pagination, IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import ColoredBox from "src/Components/Boxes/TopProductBox/ColoredBox";
import { getUrl } from "src/Utils/Common";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import PropTypes from "prop-types";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import MdRenderer from "src/Components/shared/shared/MdRenderer";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      padding: "5px",
      color: theme.palette.primary.dark,
      fontSize: theme.typography.fontSize.xxsmall,
      textAlign: "center",
      justifyContent: "center",
    },
    [`&.${tableCellClasses.body}`]: {
      textTransform: "capitalize",
      color: theme.palette.primary.dark,
      fontSize: 10,
      textAlign: "center",
      padding: "3px",
    },
  }));

  const StyledTableRow = styled(TableRow)(() => ({
    height: 15,
  }));

function PromotionSkusTable({
  manufacturer,
  promoType,
  selectedBrand,
  selectedClass
}) {

  const projectId = useSelector((state) => state.userProject.projectId);
  const[data, setData] = useState([]);
  const[loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedSku, setSelectedSku] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [chartData, setChartData] = useState({});
  const [loadingChart, setLoadingChart] = useState(false);
  const [totalData, setTotalData] = useState(null);
  const [recommendations, setRecommendations] = useState("");
  const [recommendationsSkuConclusion, setRecommendationsSkuConclusion] = useState("");
  const [recommendationsSkuFinancial, setRecommendationsSkuFinancial] = useState("");
  const [totalRecommendation, setTotalRecommendation] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [showMoreSku, setShowMoreSku] = useState(false);
  const [recommendationConclusion, setRecommendationConclusion] = useState("");
  const [recommendationFinancial, setRecommendationFinancial] = useState("");

  const formatNumber = (num) => {
    if (typeof num === 'number' && !isNaN(num)) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    }
    return num;
  };

  const formatNumberNoDecimals = (num) => {
    if (typeof num === 'number' && !isNaN(num)) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
    return num;
  };
  
  const handleOpen = (sku) => {
    setSelectedSku(sku);
    setOpen(true);
    fetchModalData(sku);
  };
  
  const handleClose = () => {
    setOpen(false);
    setOpenModal(false);
    setModalData([]);
    setSelectedSku("");
    setCurrentPage(0);
    setTotalData(null);
    setRecommendations("");
  };

  const handleChangePage = (event, value) => {
    setCurrentPage(value - 1);
  };


  const fetchAllChartData = async () => {
    setLoadingChart(true);
    
    const filters = { manufacturer: manufacturer };
    if (selectedBrand) filters.brand = selectedBrand;
    if (selectedClass) filters.class = selectedClass;
    
    const requestBody = {
      project_id: projectId,
      hierarchy: "sku_db",
      filters: filters,
      time_period: "P12M",
      promo_type: [promoType]
    };
    
    try {
      const response = await fetch(`${getUrl()}/strategy_creation/promo_evolution_by_week`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify(requestBody)
      });

      const text = await response.text();
      const cleaned = text.replace(/\bNaN\b/g, "null");
      const json = JSON.parse(cleaned);
      
      console.log("[PromotionSKUsTable] Chart API response:", json);
      
      const chartDataArray = json.data || [];
      
      const groupedData = {};
      chartDataArray.forEach(item => {
        const sku = item.sku_db;
        if (!groupedData[sku]) {
          groupedData[sku] = [];
        }
        
        const weekNumber = item.Time_Granularity;
        const year = item.year || new Date().getFullYear();
        
        const startDate = new Date(year, 0, 1); 
        const weekDate = new Date(startDate);
        
        if (weekNumber === 52) {
          weekDate.setDate(startDate.getDate() + (51 * 7) - 1 );
        } else {
          weekDate.setDate(startDate.getDate() + (weekNumber * 7));
        }
        
        const formattedDate = `${(weekDate.getMonth() + 1).toString().padStart(2, '0')}/${weekDate.getDate().toString().padStart(2, '0')}/${weekDate.getFullYear()}`;
        
        groupedData[sku].push({
          week: formattedDate,
          originalWeek: item.Time_Granularity,
          originalYear: item.year,
          VSOD: Number((Number(item.VSOD) || 0).toFixed(2)),
          baseline_volume: Number((Number(item.baseline_volume) || 0).toFixed(2))
        });
      });
      
      Object.keys(groupedData).forEach(sku => {
        groupedData[sku].sort((a, b) => {
          const dateA = new Date(a.week);
          const dateB = new Date(b.week);
          return dateA - dateB;
        });
      });
      
      setChartData(groupedData);
    } catch (error) {
      console.error("Chart API fetch error:", error);
    } finally {
      setLoadingChart(false);
    }
  };

  const handleRowToggle = (sku, rowIndex) => {
    const newExpandedRows = new Set(expandedRows);
    
    if (expandedRows.has(rowIndex)) {
      newExpandedRows.delete(rowIndex);
    } else {
      newExpandedRows.add(rowIndex);
    }
    setExpandedRows(newExpandedRows);
  };

  const fetchModalData = async (sku) => {
    if (!sku) return;
    
    setModalLoading(true);
    
    const requestBody = {
      project_id: projectId,
      hierarchy: "sku_db",
      filters: {
        sku_db: sku
      },
      time_period: "P12M",
      promo_type: [promoType]
    };

    try {
      const response = await fetch(`${getUrl()}/strategy_creation/promo_evolution_by_week`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify(requestBody)
      });

      const text = await response.text();
      const cleaned = text.replace(/\bNaN\b/g, "null");
      const json = JSON.parse(cleaned);
      
      
      console.log("[PromotionSKUsTable] Modal API response:", json);
      
      const dataArray = json.data || [];
      const to2 = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
      };
      
      const filteredRows = [];
      
      dataArray.forEach(item => {
        if (item.gap_flag === 1) {
          const processedItem = {
            sku_db: item.sku_db,
            year: item.year,
            Time_Granularity: item.Time_Granularity,
            value_sales: to2(item.value_sales),
            volume_sales: to2(item.volume_sales),
            volume_sales_non_promo: to2(item.volume_sales_non_promo),
            VSOD: to2(item.VSOD),
            baseline_volume: to2(item.baseline_volume),
            uplift_volume: to2(item.uplift_volume),
            count_PX: to2(item[`count_${promoType}`]),
            value_sales_PX: to2(item[`value_sales_${promoType}`]),
            volume_sales_PX: to2(item[`volume_sales_${promoType}`]),
            uplift_volume_PX: to2(item[`uplift_volume_${promoType}`]),
            productivity_PX: to2(item[`productivity_${promoType}`]),
            gap_flag: item.gap_flag,
            recommendation: item.RECOMMENDATIONS
          };
          filteredRows.push(processedItem);
        }
      });
      
      console.log("[PromotionSKUsTable] Filtered modal rows (gap_flag=1):", filteredRows.length);
      setModalData(filteredRows);
      
      const skuTotalData = json.total ? json.total.find(item => item.sku_db === sku) : null;
      setTotalData(skuTotalData);
      setRecommendations(json.recommendations.full_text || "");
      setRecommendationsSkuConclusion(json.recommendations.conclusion || "")
      setRecommendationFinancial(json.recommendations.financial_insights || "")
    } catch (error) {
      console.error("Modal API fetch error:", error);
      setModalData([]);
    } finally {
      setModalLoading(false);
    }
  };
    
  useEffect(()=>{
    if (!manufacturer || (!selectedBrand && !selectedClass) || !promoType) {
      console.warn("[PromotionSKUsTable] Missing required props", { manufacturer, selectedBrand, selectedClass, promoType });
      setData([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    const filters = { manufacturer: manufacturer };
    if (selectedBrand) filters.brand = selectedBrand;
    if (selectedClass) filters.class = selectedClass;
    
    const requestBody = {
      project_id: projectId,
      hierarchy: "sku_db",
      filters: filters,
      time_period: "P12M",
      promo_type: [promoType]
    };
    
    console.log("[PromotionSKUsTable] Request body:", requestBody);
    
    fetch(`${getUrl()}/strategy_creation/promo_evolution_by_week`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
      },
      body: JSON.stringify(requestBody)
    })
      .then((res) => res.text())
      .then((text) => {
        try {
          const cleaned = text.replace(/\bNaN\b/g, "null");
          if (!cleaned.trim().startsWith("{") && !cleaned.trim().startsWith("[")) {
            console.error("SKUs API did not return JSON:", cleaned);
            setData([]);
            return;
          }
          const json = JSON.parse(cleaned);
          console.log("[PromotionSKUsTable] API response:", json);
          setTotalRecommendation(json["recommendations"]["full_text"]);
          setRecommendationConclusion(json["recommendations"]["conclusion"]);
          setRecommendationFinancial(json["recommendations"]["financial_insights"]);
          
          
          const totalData = json.total || [];
          const rows = [];
          
          for (let i = 0; i < totalData.length; i++) {
            const item = totalData[i];
            const row = {
              sku_db: item.sku_db,
              discount: Number(item.discount?.toFixed(2)) || 0,
              total_value_sales: Number(item.total_value_sales) || 0,
              total_volume_sales: Number(item.total_volume_sales) || 0,
              total_negative_promo_weeks: Number(item.total_negative_promo_weeks) || 0,
              total_productivity: Number(item.total_productivity) || 0
            };
            rows.push(row);
          }
          
          console.log("[PromotionSKUsTable] Processed rows:", rows.length);
          setData(rows);
        } catch (e) {
          console.error("SKUs API parse error:", e);
          setData([]);
        }
      })
      .catch((err) => {
        console.error("PromotionSKUsTable fetch error:", err);
        setData([]);
      })
      .finally(() => {
        setLoading(false)
        window.scrollTo({
          top: document.body.scrollHeight, // scroll to bottom
          behavior: "smooth", // smooth scroll
        });
      });
  },
[manufacturer, selectedBrand, selectedClass, promoType, projectId]
)

  useEffect(() => {
    if (data.length > 0) {
      fetchAllChartData();
    }
  }, [data, manufacturer, selectedBrand, selectedClass, promoType, projectId]);


    return (
      <>
        <Typography textAlign="center" sx={{ mt: 2 }}>
          SKUs
        </Typography>

        {loading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 2 }}
          >
            <FacebookCircularProgress />
          </Box>
        ) : (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ py: 2 }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setOpenModal(true);
                }}
              >
                Recommendation for SKUs
              </Button>
            </Box>
            <TableContainer
              sx={{
                maxHeight: 600,
                overflowY: "auto",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                touchAction: "manipulation",
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <Table stickyHeader aria-label="customized table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f1f1f1" }}>
                    <StyledTableCell>#</StyledTableCell>
                    <StyledTableCell>SKU</StyledTableCell>
                    <StyledTableCell>Discount</StyledTableCell>
                    <StyledTableCell>Total Value Sales</StyledTableCell>
                    <StyledTableCell>Total Volume Sales</StyledTableCell>
                    <StyledTableCell>Invesment</StyledTableCell>
                    <StyledTableCell>ROI</StyledTableCell>
                    <StyledTableCell>Actions</StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading ? (
                    (() => {
                      // Filter out SKUs where total_promo_weeks === 0
                      const filteredData = data.filter((item) => item.total_negative_promo_weeks !== 0);

                      if (filteredData.length === 0) {
                        return (
                          <StyledTableRow>
                            <TableCell
                              colSpan={6}
                              sx={{
                                textAlign: "center",
                                fontWeight: "bold",
                                color: "#666",
                                fontSize: "12px",
                              }}
                            >
                              No underperforming SKUs for the selected brand/class
                            </TableCell>
                          </StyledTableRow>
                        );
                      }

                      // Render filtered rows
                      return filteredData.map((item, idx) => {
                        const cellSx = { fontWeight: "bold" };
                        const isExpanded = expandedRows.has(idx);

                        return (
                          <React.Fragment key={idx}>
                            <StyledTableRow>
                              <StyledTableCell sx={cellSx}>{idx + 1}</StyledTableCell>
                              <StyledTableCell sx={cellSx}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRowToggle(item.sku_db, idx)}
                                    sx={{
                                      padding: 0.5,
                                      color: "#1976d2",
                                      "&:hover": {
                                        backgroundColor: "#e3f2fd",
                                        color: "#1565c0",
                                      },
                                    }}
                                  >
                                    {isExpanded ? (
                                      <ExpandLess sx={{ fontSize: "1rem" }} />
                                    ) : (
                                      <ExpandMore sx={{ fontSize: "1rem" }} />
                                    )}
                                  </IconButton>
                                  {item.sku_db}
                                </Box>
                              </StyledTableCell>

                              <StyledTableCell sx={cellSx}>
                                <ColoredBox
                                  number={formatNumber(item.discount * 100)}
                                  Color={
                                    item.discount > 0
                                      ? "#0BB783"
                                      : item.discount === 0
                                      ? "#0095FF"
                                      : "#B22234"
                                  }
                                />
                              </StyledTableCell>

                              <StyledTableCell sx={cellSx}>
                                {formatNumberNoDecimals(item.total_value_sales)}
                              </StyledTableCell>

                              <StyledTableCell sx={cellSx}>
                                {formatNumberNoDecimals(item.total_volume_sales)}
                              </StyledTableCell>

                              <StyledTableCell sx={cellSx}>N/A</StyledTableCell>
                              <StyledTableCell sx={cellSx}>N/A</StyledTableCell>

                              

                              <StyledTableCell sx={cellSx}>
                                <Box display="flex" justifyContent="center" gap={1}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    // disabled={item.total_negative_promo_weeks <= 0}
                                    disabled={true}
                                    onClick={() => handleOpen(item.sku_db)}
                                  >
                                    Recommendation
                                  </Button>
                                </Box>
                              </StyledTableCell>
                            </StyledTableRow>

                            {/* Expanded Row */}
                            {isExpanded && (
                              <StyledTableRow>
                                <StyledTableCell colSpan={6} sx={{ padding: 0 }}>
                                  <Box sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                                    {loadingChart ? (
                                      <Box display="flex" justifyContent="center" sx={{ py: 2 }}>
                                        <FacebookCircularProgress />
                                      </Box>
                                    ) : chartData[item.sku_db]?.length > 0 ? (
                                      <Box>
                                        <Typography
                                          variant="h6"
                                          sx={{ mb: 2, textAlign: "center" }}
                                        >
                                          Weekly Performance Chart - {item.sku_db}
                                        </Typography>
                                        <Box sx={{ height: 300 }}>
                                          <ReactApexChart
                                            options={{
                                              chart: {
                                                type: "scatter",
                                                toolbar: { show: true },
                                                height: 300,
                                              },
                                              xaxis: {
                                                categories: chartData[item.sku_db].map(
                                                  (d) => d.week
                                                ),
                                                title: {
                                                  text: "Date",
                                                  style: { fontSize: "12px" },
                                                },
                                                labels: {
                                                  style: { fontSize: "10px" },
                                                  rotate: -45,
                                                },
                                              },
                                              yaxis: {
                                                title: {
                                                  text: "Values",
                                                  style: { fontSize: "12px" },
                                                },
                                                labels: { style: { fontSize: "10px" } },
                                              },
                                              stroke: { width: 0 },
                                              markers: {
                                                size: 3,
                                                strokeWidth: 2,
                                                strokeColors: ["#00265E", "#fbc210"],
                                                fillColors: ["#00265E", "#fbc210"],
                                                hover: { size: 8 },
                                              },
                                              colors: ["#00265E", "#fbc210"],
                                              legend: {
                                                position: "top",
                                                horizontalAlign: "center",
                                              },
                                              grid: { show: true, borderColor: "#e0e0e0" },
                                              tooltip: { shared: true, intersect: false },
                                            }}
                                            series={[
                                              {
                                                name: "VSOD",
                                                data: chartData[item.sku_db].map(
                                                  (d) => d.VSOD
                                                ),
                                              },
                                            ]}
                                            type="scatter"
                                            height={300}
                                          />
                                        </Box>
                                      </Box>
                                    ) : (
                                      <Box
                                        display="flex"
                                        justifyContent="center"
                                        sx={{ py: 2 }}
                                      >
                                        <Typography variant="body2" color="text.secondary">
                                          No chart data available for {item.sku_db}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </StyledTableCell>
                              </StyledTableRow>
                            )}
                          </React.Fragment>
                        );
                      });
                    })()
                  ) : (
                    <StyledTableRow>
                      <TableCell colSpan={6} sx={{ textAlign: "center" }}>
                        Loading...
                      </TableCell>
                    </StyledTableRow>
                  )}
                </TableBody>

              </Table>
            </TableContainer>
          </>
        )}
        <Modal open={openModal} onClose={handleClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 2,
              maxWidth: 1100,
              width: "100%",
              maxHeight: "80vh",
              overflow: "auto",
              p: 4,
            }}
          >
            <Box sx={{ flex: "0 0 50%" }}>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                Recommendation Overview
              </Typography>
              <MdRenderer
                mdString={recommendationConclusion}
                modalContent={recommendationConclusion}
                showMoreButton={false}
              />
            </Box>
            {showMore && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" fontWeight={700} color="primary.main" gutterBottom>
              Detailed Insights
            </Typography>
            <MdRenderer
              mdString={totalRecommendation}
              modalContent={totalRecommendation}
              showMoreButton={false}
            />
          </Box>
        )}

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowMore((prev) => !prev)}
          >
            {showMore ? "Hide Details" : "View More"}
          </Button>
        </Box>
          </Box>
        </Modal>
        <Modal open={open} onClose={handleClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 2,
              maxWidth: "90vw",
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              p: 2,
            }}
          >
            {modalLoading ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{ py: 4 }}
              >
                <FacebookCircularProgress />
              </Box>
            ) : (
              <>
                {/* Section 1 */}
                <Box
                  display="flex"
                  flexDirection={{ xs: "column", lg: "row" }}
                  gap={2}
                  sx={{ mb: 3 }}
                >
                  {/* Table Section */}
                  <Box sx={{ flex: "0 0 60%", minWidth: 0}}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color="primary.main"
                      sx={{ mb: 2 }}
                    >
                      Detailed Data
                    </Typography>
                    <TableContainer
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        maxHeight: 400,
                        overflow: "auto",
                      }}
                    >
                      <Table
                        size="small"
                        sx={{
                          "& .MuiTableCell-root": {
                            width: "12.5%",
                            fontSize: "0.65rem",
                            padding: "3px 4px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "grey.100" }}>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                textAlign: "center",
                              }}
                            >
                              Week
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                textAlign: "center",
                              }}
                            >
                              Value Sales
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                textAlign: "center",
                              }}
                            >
                              Volume Sales
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                textAlign: "center",
                              }}
                            >
                              Volume Sales Non Promo
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                textAlign: "center",
                              }}
                            >
                              VSOD
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                textAlign: "center",
                              }}
                            >
                              Count {promoType}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                textAlign: "center",
                              }}
                            >
                              Value Sales {promoType}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                textAlign: "center",
                              }}
                            >
                              Volume Sales {promoType}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                textAlign: "center",
                              }}
                            >
                              Uplift Volume {promoType}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                textAlign: "center",
                              }}
                            >
                              Productivity {promoType}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                textAlign: "center",
                              }}
                            >
                              Recommentation {promoType}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {modalLoading ? (
                            <TableRow>
                              <TableCell
                                colSpan={10}
                                sx={{ textAlign: "center", py: 2 }}
                              >
                                <FacebookCircularProgress />
                              </TableCell>
                            </TableRow>
                          ) : modalData.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={10}
                                sx={{
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  color: "#666",
                                }}
                              >
                                No data available for {selectedSku}
                              </TableCell>
                            </TableRow>
                          ) : (
                            modalData
                              .slice(
                                currentPage * rowsPerPage,
                                currentPage * rowsPerPage + rowsPerPage
                              )
                              .map((item, idx) => (
                                <TableRow key={idx} hover>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {
                                      item.Time_Granularity
                                    }
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {formatNumberNoDecimals(item.value_sales)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {formatNumberNoDecimals(item.volume_sales)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {formatNumberNoDecimals(item.volume_sales_non_promo)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {formatNumberNoDecimals(item.VSOD)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {formatNumber(item.count_PX)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {formatNumberNoDecimals(item.value_sales_PX)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {formatNumberNoDecimals(item.volume_sales_PX)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {formatNumberNoDecimals(item.uplift_volume_PX)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {/*formatNumber(item.productivity_PX)*/}

                                    <ColoredBox
                                        number={formatNumber(item.productivity_PX)}
                                        Color={
                                          item.productivity_PX > 0
                                            ? "#0BB783"
                                            : item.productivity_PX === 0
                                            ? "#0095FF"
                                            : "#B22234"
                                        }
                                      />
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {item.recommendation}
                                  </TableCell>
                                </TableRow>
                              ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {modalData.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          mt: 2,
                          mb: 2,
                        }}
                      >
                        <Pagination
                          count={Math.ceil(modalData.length / rowsPerPage)}
                          page={currentPage + 1}
                          onChange={handleChangePage}
                          color="primary"
                          shape="rounded"
                          size="small"
                          showFirstButton
                          showLastButton
                          siblingCount={1}
                          boundaryCount={1}
                          sx={{
                            "& .MuiPaginationItem-root": {
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              minWidth: "28px",
                              height: "28px",
                              margin: "0 1px",
                            },
                            "& .MuiPagination-ul": {
                              flexWrap: "wrap",
                              justifyContent: "center",
                            },
                            "& .MuiPaginationItem-page.Mui-selected": {
                              backgroundColor: "#1976d2",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Chart Section */}
                  <Box sx={{ flex: "0 0 40%", minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color="primary.main"
                      sx={{ mb: 2, textAlign: "center" }}
                    >
                      Performance Overview
                    </Typography>
                    {(() => {
                      const totalNegativeWeeks =
                        totalData?.total_negative_promo_weeks || 0;
                      const totalNonPromoWeeks =
                        totalData?.total_non_promo_weeks || 0;
                      const totalPositiveWeeks =
                        totalData?.total_positive_promo_weeks || 0;

                      const chartOptions = {
                        chart: {
                          type: "pie",
                          toolbar: { show: false },
                        },
                        colors: ["#B22234", "#0BB783", "#0095FF"],
                        dataLabels: {
                          enabled: true,
                          style: {
                            fontSize: "10px",
                            fontWeight: "bold",
                            colors: ["#fff"],
                          },
                        },
                        labels: [
                          "Negative Promo Weeks",
                          "Positive Promo Weeks",
                          "Non Promo Weeks",
                        ],
                        legend: {
                          show: true,
                          position: "bottom",
                          fontSize: "10px",
                          itemMargin: {
                            horizontal: 5,
                            vertical: 5,
                          },
                        },
                        plotOptions: {
                          pie: {
                            donut: {
                              size: "60%",
                            },
                            expandOnClick: false,
                          },
                        },
                        responsive: [
                          {
                            breakpoint: 480,
                            options: {
                              chart: {
                                width: 200,
                              },
                              legend: {
                                position: "bottom",
                              },
                            },
                          },
                        ],
                      };

                      const chartSeries = [
                        totalNegativeWeeks,
                        totalPositiveWeeks,
                        totalNonPromoWeeks,
                      ];
                      const totalWeeks =
                        totalNegativeWeeks +
                        totalPositiveWeeks +
                        totalNonPromoWeeks;

                      return (
                        <Box sx={{ width: "100%" }}>
                          {totalWeeks > 0 ? (
                            <ReactApexChart
                              options={chartOptions}
                              series={chartSeries}
                              type="pie"
                              height={250}
                            />
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: 250,
                                color: "#666",
                              }}
                            >
                              <Typography variant="body2">
                                No data available for chart
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>

                {/* Recommendations */}
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary.main"
                    sx={{ mb: 2 }}
                  >
                    Recommendation Overview
                  </Typography>
                  <MdRenderer
                    mdString={recommendationConclusion}
                    modalContent={recommendationConclusion}
                    showMoreButton={false}
                  />
                </Box>
                {showMoreSku && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main" gutterBottom>
                      Detailed Insights
                    </Typography>
                    <MdRenderer
                      mdString={recommendations}
                      modalContent={recommendations}
                      showMoreButton={false}
                    />
                  </Box>
                )}

                <Box sx={{ mt: 3, textAlign: "center" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setShowMoreSku((prev) => !prev)}
                  >
                    {showMoreSku ? "Hide Details" : "View More"}
                  </Button>
                </Box>

                <Box mt={3} textAlign="right">
                  <Button
                    onClick={handleClose}
                    variant="contained"
                    sx={{
                      mt: 2,
                      textTransform: "none",
                      backgroundColor: "#00265E",
                      color: "#fafbfc",
                      fontWeight: "bold",
                      boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                      borderRadius: "8px",
                      paddingX: 2,
                      paddingY: 1,
                      "&:hover": {
                        color: "#ffffff",
                        backgroundColor: "#001433",
                        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
                      },
                    }}
                  >
                    Close
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Modal>
      </>
    );
}

export default PromotionSkusTable;

PromotionSkusTable.propTypes = {
  manufacturer: PropTypes.string.isRequired,
  promoType: PropTypes.string.isRequired,
  selectedBrand: PropTypes.string.isRequired,
  selectedClass: PropTypes.string,
};